import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { useApolloQuery } from "../../../../../hooks";
import { toastHandler } from "../../../../../utils/helpers";
import { toastTypesKeys } from "../../../../../utils/constants";
import { ToastContainer } from "react-toastify";

const CREATE_POLICY_CATEGORY = gql`
  mutation CreatePolicyCategory($input: PolicyCategoryCreateInput!) {
    policyCategoryCreate(input: $input) {
      clientMutationId
      policyCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_POLICYCATEGORY = gql`
  query GetPolicyCategory($id: ID!) {
    policyCategory(id: $id) {
      id
      name
      geographicalAreaId
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_POLICYCATEGORY = gql`
  mutation UpdatePolicyCategory(
    $id: ID!
    $name: String!
    $geographicalAreaId: ID!
  ) {
    policyCategoryUpdate(
      input: {
        id: $id
        policyCategoryInput: {
          name: $name
          geographicalAreaId: $geographicalAreaId
        }
      }
    ) {
      clientMutationId
      policyCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const GEOGRAPHICALAREA = gql`
  query GetGeographicalAreas {
    geographicalAreas {
      createdAt
      id
      name
      policyCategories {
        createdAt
        id
        name
        updatedAt
      }
      updatedAt
    }
  }
`;

function PolicyCategoriesAction({ isUpdate = false }) {
  const notify = useNotify();
  const navigate = useNavigate();
  const { user } = useAuthSession() as any;
  const { translator, currentLanguage } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const policy_category_id = searchParams.get("id");

  const { data: geographicalareaList } = useApolloQuery({
    queryDocument: GEOGRAPHICALAREA,
  });

  const modifiedGeographicalAreaList =
    geographicalareaList?.geographicalAreas?.map((item: any) => {
      return {
        value: item?.id,
        label: item?.name,
      };
    });

  const { data: policyCategoryData, refetch: policyCategoryDataCall } =
    useQuery(GET_POLICYCATEGORY, {
      variables: { id: policy_category_id },
      skip: !policy_category_id,
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    });

  const [createPolicyCategories, { loading, error }] = useMutation(
    CREATE_POLICY_CATEGORY,
    {
      context: {
        headers: {
          "accept-language": currentLanguage || "en",
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updatePolicyCategory] = useMutation(UPDATE_POLICYCATEGORY, {
    onCompleted: () => {
      policyCategoryDataCall();
    },
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!policyCategoryData?.policyCategory) return undefined;

    const { name, geographicalAreaId } = policyCategoryData?.policyCategory;

    return {
      policy_categories: name,
      geographicalAreaId: geographicalAreaId,
    };
  }, [policyCategoryData]);

  const form = useForm<any>({
    defaultValues: {
      policy_categories: "",
      geographicalAreaId: null,
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updatePolicyCategory({
          variables: {
            id: Number(policy_category_id),
            name: data?.policy_categories,
            geographicalAreaId: Number(data?.geographicalAreaId),
          },
        });
        if (response.data) {
          notify.success(`Policy Category Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/policy-categories");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    try {
      const response = await createPolicyCategories({
        variables: {
          input: {
            policyCategoryInput: {
              name: data?.policy_categories,
              geographicalAreaId: data?.geographicalAreaId,
            },
          },
        },
      });
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Policy Category Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/policy-categories");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Policy Category Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel>
                {translator("Policy Category")}{" "}
                {translator("dashboardTableHead.Name")}
              </FormLabel>
              <OutlinedInput
                {...form?.register("policy_categories", {
                  required: "Required!",
                })}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel> {translator("Geographical Area")}</FormLabel>
              <Controller
                name="geographicalAreaId"
                control={form?.control}
                rules={{ required: "Required!" }}
                render={({ field }: any) => (
                  <Select {...field} displayEmpty>
                    {modifiedGeographicalAreaList?.map((op: any, i: any) => (
                      <MenuItem key={`${op.value}-${i}`} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {form.formState.errors.geographicalAreaId?.message && (
                <FormHelperText>
                  {String(form.formState.errors.geographicalAreaId?.message)}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/policy-categories"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </>
  );
}

export { PolicyCategoriesAction };
